import type { ReactNode } from "react";
import { useEffect, useRef, useState } from "react";
import { cva } from "class-variance-authority";
import { motion } from "motion/react";

import * as styles from "./Fireworks.module.scss";

const randomBetween = (min: number, max: number) =>
  min + Math.random() * (max - min);

const randomIntBetween = (min: number, max: number) =>
  Math.floor(randomBetween(min, max + 1));

const getRandomElementFromArray = <T,>(arr: readonly T[]): T =>
  arr[randomIntBetween(0, arr.length - 1)];

const RAY_VARIANTS = {
  pink: styles.pink,
  purple: styles.purple,
  blue: styles.blue,
  yellow: styles.yellow,
  orange: styles.orange,
  red: styles.red,
} as const;

const RAY_VARIANT_OPTIONS = Object.keys(RAY_VARIANTS) as Array<
  keyof typeof RAY_VARIANTS
>;

const rayVariants = cva(styles.ray, {
  variants: {
    variant: RAY_VARIANTS,
  },
  defaultVariants: {
    variant: "red",
  },
});

type Burst = {
  id: string;
  rays: number;
  top: string;
  left: string;
  repeatDelay: number;
  variant: (typeof RAY_VARIANT_OPTIONS)[number];
};

const MAX_ITERATIONS_COUNT = 3;
const DURATION = 2;

const getNewUid = () => `${Date.now()}-${Math.random().toString(16).slice(2)}`;

const makeBursts = (): Burst[] => {
  const count = randomIntBetween(1, 3);
  return Array.from({ length: count }, (_, i) => {
    return {
      id: `${getNewUid()}-${i}`,
      rays: randomIntBetween(5, 12),
      top: `${randomIntBetween(15, 85)}%`,
      left: `${randomIntBetween(15, 85)}%`,
      variant: getRandomElementFromArray(RAY_VARIANT_OPTIONS),
      repeatDelay: Math.round(randomBetween(1.8, 3.5) * 1000),
    };
  });
};

export const Fireworks = ({
  children,
  isEnabled = true,
}: {
  children: ReactNode;
  isEnabled?: boolean;
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const iterationCount = useRef<number>(0);
  const [isVisible, setIsVisible] = useState(false);
  const [bursts, setBursts] = useState<Burst[]>([]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) {
      return;
    }

    let wasIntersecting = false;

    const obs = new IntersectionObserver(
      ([entry]) => {
        const isIntersecting = entry.isIntersecting;

        if (!wasIntersecting && isIntersecting) {
          iterationCount.current = 0;
        }

        wasIntersecting = isIntersecting;
        setIsVisible(isIntersecting);
      },
      { threshold: 0.2 },
    );

    obs.observe(el);
    return () => obs.disconnect();
  }, [isEnabled]);

  useEffect(() => {
    if (!isVisible) {
      return;
    }

    let timeoutId: number | undefined;

    const tick = () => {
      if (iterationCount.current >= MAX_ITERATIONS_COUNT) {
        if (timeoutId !== undefined) {
          window.clearTimeout(timeoutId);
        }
        setIsVisible(false);
        return;
      }

      iterationCount.current += 1;

      const next = makeBursts();
      setBursts(next);

      const maxDelayMs = next.reduce((m, b) => Math.max(m, b.repeatDelay), 0);

      timeoutId = window.setTimeout(tick, maxDelayMs + DURATION * 1000);
    };

    tick();

    return () => {
      if (timeoutId !== undefined) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [isVisible]);

  if (!isEnabled) {
    return <>{children}</>;
  }

  const renderBurst = (
    { id, rays, top, left, variant }: Burst,
    index: number,
  ) => {
    const step = 360 / rays;

    return (
      <motion.div
        key={`burst-${id}`}
        className={styles.absoluteContainer}
        style={{
          top,
          left,
        }}
        animate={{
          opacity: [0, 1, 1, 1, 0],
          scale: [0, 0.2, 2, 2, 2],
          transition: {
            ease: "easeOut",
            duration: DURATION,
            delay: 0.2 * index,
          },
        }}
      >
        {Array.from({ length: rays }, (_, i) => (
          <span
            key={`${id}-${i}`}
            className={styles.absoluteContainer}
            style={{
              transform: `translate(-50%, -50%) rotate(${step * i}deg)`,
            }}
          >
            <span
              className={rayVariants({ variant })}
              data-animation-class="ray-fade"
            />
          </span>
        ))}
      </motion.div>
    );
  };

  const renderBursts = isVisible
    ? bursts.map((burst, index) => renderBurst(burst, index))
    : null;

  return (
    <div ref={containerRef} className={styles.fireworksContainer}>
      <div className={styles.fireworks}>{renderBursts}</div>
      <div className={styles.childrenContainer}>{children}</div>
    </div>
  );
};
